import { css } from "@emotion/react"
import {
  Button,
  Inline,
  Stack,
  Text,
  tokens,
  VisuallyHidden,
} from "@alexandrudanpop/byfolio-ui"
import React, { useEffect, useRef, useState, type ReactNode } from "react"
import type { AnalyticsConsentChoice } from "./consent-store"
import {
  getAnalyticsConsentChoice,
  setAnalyticsConsentChoice,
  subscribeToAnalyticsConsent,
  subscribeToAnalyticsConsentReview,
} from "./runtime"

interface AnalyticsConsentProps {
  readonly children: ReactNode
}

const shellStyles = css`
  position: fixed;
  z-index: ${tokens.zIndex.chrome - 1};
  right: max(1rem, env(safe-area-inset-right));
  bottom: max(1rem, env(safe-area-inset-bottom));
  left: max(1rem, env(safe-area-inset-left));
  width: min(34rem, calc(100% - 2rem));
  max-height: min(28rem, calc(100dvh - 2rem));
  margin-left: auto;
  overflow: auto;
  padding: clamp(0.75rem, 2vw, 1rem);
  color: var(--byfolio-text);
  border: 1px solid var(--byfolio-border-strong);
  border-radius: var(--byfolio-radius-lg);
  background: var(--byfolio-surface-elevated);
  box-shadow: var(--byfolio-shadow-lg);
  overscroll-behavior: contain;

  @media (max-width: 28rem) {
    right: max(0.5rem, env(safe-area-inset-right));
    bottom: max(0.5rem, env(safe-area-inset-bottom));
    left: max(0.5rem, env(safe-area-inset-left));
    width: calc(100% - 1rem);
  }
`

const headingStyles = css`
  margin: 0;
  color: var(--byfolio-text);
  font-size: var(--byfolio-font-size-xl);
  line-height: var(--byfolio-line-height-heading);
`

const bodyStyles = css`
  margin: 0;
  color: var(--byfolio-text-muted);
  font-size: 0.9375rem;
`

const actionStyles = css`
  & > button {
    flex: 1 1 0;
    min-width: 0;
    min-height: 44px;
    padding-inline: 0.5rem;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }
`

const AnalyticsConsent = ({ children }: AnalyticsConsentProps) => {
  const [mounted, setMounted] = useState(false)
  const [choice, setChoice] = useState<AnalyticsConsentChoice>("unset")
  const [reviewing, setReviewing] = useState(false)
  const [announcement, setAnnouncement] = useState("")
  const panelRef = useRef<HTMLDivElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    setChoice(getAnalyticsConsentChoice())
    const unsubscribeChoice = subscribeToAnalyticsConsent(setChoice)
    const unsubscribeReview = subscribeToAnalyticsConsentReview(() => {
      returnFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null
      setReviewing(true)
    })
    setMounted(true)
    return () => {
      unsubscribeChoice()
      unsubscribeReview()
    }
  }, [])

  useEffect(() => {
    if (reviewing) panelRef.current?.focus()
  }, [reviewing])

  const decide = (nextChoice: "accepted" | "rejected") => {
    const returnFocus = reviewing ? returnFocusRef.current : null
    setAnalyticsConsentChoice(nextChoice)
    setReviewing(false)
    setAnnouncement(
      nextChoice === "accepted"
        ? "Analytics accepted. Your choice was saved."
        : "Analytics rejected. Your choice was saved."
    )
    window.requestAnimationFrame(() => {
      if (returnFocus?.isConnected) {
        returnFocus.focus()
      } else {
        document.querySelector<HTMLButtonElement>("[data-test=menu]")?.focus()
      }
    })
  }

  const showPanel = mounted && (choice === "unset" || reviewing)

  return (
    <>
      {children}
      {showPanel && (
        <div
          aria-labelledby="analytics-choice-title"
          aria-describedby="analytics-choice-description"
          css={shellStyles}
          data-test="analytics-consent"
          ref={panelRef}
          role="region"
          tabIndex={reviewing ? -1 : undefined}
        >
          <Stack gap="sm">
            <h2 id="analytics-choice-title" css={headingStyles}>
              Analytics
            </h2>
            <Text id="analytics-choice-description" css={bodyStyles}>
              I use Google Analytics to understand which pages are useful. It
              won’t load unless you accept.
            </Text>
            <Inline gap="sm" wrap={false} css={actionStyles}>
              <Button
                aria-pressed={choice === "rejected"}
                size="sm"
                tone="neutral"
                variant="outline"
                onClick={() => {
                  decide("rejected")
                }}
              >
                Reject analytics
              </Button>
              <Button
                aria-pressed={choice === "accepted"}
                size="sm"
                tone="neutral"
                variant="outline"
                onClick={() => {
                  decide("accepted")
                }}
              >
                Accept analytics
              </Button>
            </Inline>
          </Stack>
        </div>
      )}
      <VisuallyHidden role="status" aria-live="polite">
        {announcement}
      </VisuallyHidden>
    </>
  )
}

export default AnalyticsConsent
